import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import './ThemeSelector.css';

const ThemeSelector: React.FC = () => {
  const { currentTheme, setTheme, themes } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleThemeSelect = (themeId: string) => {
    setTheme(themeId);
    setIsOpen(false);
  };

  // Organize themes into categories
  const standardDarkThemes = themes.filter(theme => 
    theme.type === 'dark' && !theme.id.startsWith('pantone-')
  );
  const pantoneDarkThemes = themes.filter(theme => 
    theme.type === 'dark' && theme.id.startsWith('pantone-')
  );
  const standardLightThemes = themes.filter(theme => 
    theme.type === 'light' && !theme.id.startsWith('pantone-')
  );
  const pantoneLightThemes = themes.filter(theme => 
    theme.type === 'light' && theme.id.startsWith('pantone-')
  );

  const renderThemeSection = (themes: any[], title: string, singleColumn = false) => (
    <div className="theme-section">
      <div className="theme-section-header">{title}</div>
      <div className={`theme-list ${singleColumn ? 'single-column' : ''}`}>
        {themes.map((theme) => (
          <button
            key={theme.id}
            className={`theme-option ${currentTheme.id === theme.id ? 'active' : ''}`}
            onClick={() => handleThemeSelect(theme.id)}
            title={theme.name}
          >
            <div className="theme-preview">
              <div 
                className="theme-color-bg" 
                style={{ backgroundColor: theme.colors.bg }}
              />
              <div 
                className="theme-color-main" 
                style={{ backgroundColor: theme.colors.main }}
              />
              <div 
                className="theme-color-sub" 
                style={{ backgroundColor: theme.colors.subColor }}
              />
            </div>
            <span className="theme-option-name">{theme.name}</span>
            {currentTheme.id === theme.id && (
              <span className="theme-check">✓</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="theme-selector" ref={dropdownRef}>
      <button 
        className="theme-selector-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span>●</span>
        <span>theme</span>
      </button>

      {isOpen && (
        <div className="theme-dropdown">
          {renderThemeSection(standardDarkThemes, 'dark themes')}
          {renderThemeSection(pantoneDarkThemes, 'pantone dark (2020-2025)', true)}
          {renderThemeSection(standardLightThemes, 'light themes')}
          {renderThemeSection(pantoneLightThemes, 'pantone light (2020-2025)', true)}
        </div>
      )}
    </div>
  );
};

export default ThemeSelector; 